// Shared AE roster for Central + Admin Home (FR-P1 / US-P2) → docs/mockups/central-dashboard.html
// and admin-dashboard.html. Lists Managers with rollup KPIs; "Open dashboard" drills into the
// AE Home view for that editor.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { StaffDashboard } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { Button, Card, CardBody, CardHeader, Spinner } from "../../components/ui.js";
import { KpiCard } from "./dashboard-ui.js";
import { AeDashboardView } from "./AeDashboard.js";

function initials(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || email.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

function domainsLabel(domains: string[]): string {
  if (domains.length === 0) return "no domain";
  return domains.join(" · ");
}

export function StaffDashboardView({
  variant,
}: {
  /** Drives copy + whether the System Status strip is shown (Admin only). Home routes only
   *  Central here since WO-2026-08-19 gave Admin its own "Journal Pipeline" page; the Admin
   *  variant stays for the /admin-side entry points and as the roster fallback. */
  variant: "Central" | "Admin";
}): JSX.Element {
  const { user } = useAuth();
  const [editor, setEditor] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: qk.dashboardStaff,
    queryFn: () => api.get<StaffDashboard>("/dashboard/staff"),
  });

  if (editor) {
    return (
      <div className="space-y-3">
        <Button type="button" variant="ghost" onClick={() => setEditor(undefined)}>
          ← Back to {variant === "Admin" ? "Admin" : "Central"} overview
        </Button>
        <AeDashboardView forcedEditor={editor} />
      </div>
    );
  }

  if (isLoading || !data) return <Spinner label="Loading staff dashboard…" />;

  const firstName = (data.viewer.name ?? user?.name)?.split(" ")[0] || variant;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold">{variant} overview 👋</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          {variant === "Admin"
            ? "AE productivity plus a glance at system health."
            : `How each Assistant Editor is doing — open any AE to step in, ${firstName}.`}
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Assistant Editors" value={data.kpis.aeCount} tone="amber" sublabel="Managers in roster" />
        <KpiCard label="Greenlits this week" value={data.kpis.greenlitsThisWeek} tone="green" sublabel="Across all AEs" />
        <KpiCard label="Active preprints" value={data.kpis.activePreprints} tone="blue" sublabel="Being shepherded" />
        <KpiCard label="Proposals to date" value={data.kpis.proposals} tone="violet" sublabel="On AE-led preprints" />
      </div>

      {variant === "Admin" && <SystemStatusStrip />}

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">👥 Assistant Editors</h2>
        <p className="text-sm text-slate-500">
          Open an AE&apos;s dashboard to see their KPIs, preprint columns, and student table.
        </p>
        <Card>
          <CardHeader className="hidden gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid md:grid-cols-[1.4fr_1fr_repeat(4,minmax(0,0.7fr))_auto]">
            <span>AE</span>
            <span>Domains</span>
            <span className="text-right">Greenlits (week)</span>
            <span className="text-right">Greenlits (to date)</span>
            <span className="text-right">Proposals</span>
            <span className="text-right">Active</span>
            <span />
          </CardHeader>
          <CardBody className="space-y-1">
            {data.aes.length === 0 ? (
              <p className="text-sm text-slate-400">No Managers in the user roster yet.</p>
            ) : (
              data.aes.map((ae) => (
                <div
                  key={ae.email}
                  className="grid items-center gap-2 rounded-md px-1 py-2 hover:bg-slate-50 md:grid-cols-[1.4fr_1fr_repeat(4,minmax(0,0.7fr))_auto]"
                >
                  <span className="flex min-w-0 items-center gap-2 truncate text-sm">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-700">
                      {initials(ae.name, ae.email)}
                    </span>
                    <span className="truncate">
                      {ae.name ?? ae.email}
                      {ae.name && <span className="ml-1 text-xs text-slate-400">{ae.email}</span>}
                    </span>
                  </span>
                  <span className="truncate text-xs text-slate-500">{domainsLabel(ae.domains)}</span>
                  <span className="text-right text-sm">{ae.greenlitsThisWeek}</span>
                  <span className="text-right text-sm font-semibold text-green-700">{ae.greenlitsToDate}</span>
                  <span className="text-right text-sm">{ae.proposals}</span>
                  <span className="text-right text-sm">{ae.activeCount}</span>
                  <Button type="button" variant="ghost" className="justify-self-end py-1 text-brand" onClick={() => setEditor(ae.email)}>
                    Open →
                  </Button>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function SystemStatusStrip(): JSX.Element {
  return (
    <Link
      to="/admin"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 hover:border-emerald-300"
    >
      <span>
        <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 align-middle" />
        <strong>System Status</strong> — open the health board for database, pipelines, and COI.
      </span>
      <span className="font-medium">Open System Status →</span>
    </Link>
  );
}
